export const CATEGORY_L1_VALUES = [
  'models',
  'apis',
  'agents',
  'frameworks',
  'tools',
  'apps',
  'data',
  'tutorials',
  'community',
  'pending',
] as const;

export type CategoryL1 = (typeof CATEGORY_L1_VALUES)[number];

export const CATEGORY_L2_VALUES = [
  'development',
  'office',
  'content_creation',
  'data_analysis',
  'design',
  'marketing',
  'security',
  'education',
] as const;

export type CategoryL2 = (typeof CATEGORY_L2_VALUES)[number];

export const CATEGORY_SOURCE_VALUES = ['manual', 'rule', 'ai'] as const;
export type CategorySource = (typeof CATEGORY_SOURCE_VALUES)[number];

export const CATEGORY_L1_PRIORITY: readonly CategoryL1[] = [
  'models',
  'apis',
  'agents',
  'frameworks',
  'tools',
  'apps',
  'data',
  'tutorials',
  'community',
  'pending',
];

export const CATEGORY_SIGNAL_WEIGHTS = {
  strong: 3,
  medium: 2,
  weak: 1,
} as const;

export const CATEGORY_CONFIDENCE_THRESHOLD = 0.5;

export const CATEGORY_L1_KEYWORDS: Readonly<Record<CategoryL1, { strong: string[]; medium: string[]; weak: string[] }>> = {
  models: {
    strong: ['foundation model', 'llm', 'language model', 'embedding model', 'multimodal model', 'model provider'],
    medium: ['inference', 'token', 'fine-tuning', 'model hub'],
    weak: ['openai', 'anthropic', 'deepseek', 'gemini'],
  },
  apis: {
    strong: ['api', 'rest api', 'sdk api', 'endpoint', 'api gateway'],
    medium: ['oauth', 'webhook', 'http client'],
    weak: ['postman', 'hoppscotch', 'insomnia'],
  },
  agents: {
    strong: ['agent', 'autonomous agent', 'multi-agent', 'workflow agent', 'assistant framework'],
    medium: ['tool calling', 'planner', 'orchestration', 'assistant'],
    weak: ['langgraph', 'crewai', 'autogen'],
  },
  frameworks: {
    strong: ['framework', 'library', 'runtime', 'sdk'],
    medium: ['plugin system', 'middleware', 'boilerplate'],
    weak: ['react', 'vue', 'next.js', 'svelte', 'nestjs'],
  },
  tools: {
    strong: ['developer tool', 'cli tool', 'productivity tool', 'dashboard', 'tooling'],
    medium: ['automation', 'editor', 'workflow', 'tool', 'plugin', 'extension'],
    weak: ['vercel', 'supabase', 'figma', 'notion'],
  },
  apps: {
    strong: ['application', 'saas', 'product', 'workspace app', 'ai app'],
    medium: ['end-user', 'business workflow', 'copilot app'],
    weak: ['web app', 'mobile app', 'desktop app'],
  },
  data: {
    strong: ['dataset', 'benchmark', 'corpus', 'data platform', 'data pipeline'],
    medium: ['etl', 'analytics', 'warehouse'],
    weak: ['kaggle', 'huggingface datasets', 'bigquery'],
  },
  tutorials: {
    strong: ['tutorial', 'course', 'guide', 'how to', 'documentation'],
    medium: ['article', 'blog', 'walkthrough', 'playbook'],
    weak: ['youtube', 'bilibili', 'medium', 'substack'],
  },
  community: {
    strong: ['community', 'forum', 'discussion', 'newsletter', 'social'],
    medium: ['chat group', 'discord server', 'slack workspace'],
    weak: ['reddit', 'x.com', 'twitter', 'producthunt', 'hackernews'],
  },
  pending: {
    strong: [],
    medium: [],
    weak: [],
  },
};

export const CATEGORY_L2_KEYWORDS: Readonly<Record<CategoryL2, string[]>> = {
  development: ['dev', 'developer', 'coding', 'programming', 'engineering', 'sdk', 'api', 'framework', 'cli'],
  office: ['office', 'workspace', 'meeting', 'document', 'notion', 'slack', 'collaboration'],
  content_creation: ['content', 'writing', 'video', 'audio', 'design asset', 'creative', 'editing'],
  data_analysis: ['data', 'analytics', 'bi', 'dashboard', 'sql', 'warehouse', 'visualization'],
  design: ['design', 'ui', 'ux', 'prototype', 'figma', 'wireframe'],
  marketing: ['marketing', 'growth', 'seo', 'ads', 'campaign', 'conversion'],
  security: ['security', 'privacy', 'vulnerability', 'compliance', 'auth', 'rbac'],
  education: ['education', 'learning', 'tutorial', 'course', 'training', 'bootcamp'],
};

export const CATEGORY_L2_ALLOWED_BY_L1: Readonly<Record<CategoryL1, readonly CategoryL2[]>> = {
  models: ['development', 'data_analysis', 'education'],
  apis: ['development', 'security', 'data_analysis'],
  agents: ['development', 'office', 'content_creation', 'marketing', 'education'],
  frameworks: ['development', 'education'],
  tools: ['development', 'office', 'content_creation', 'data_analysis', 'design', 'marketing', 'security', 'education'],
  apps: ['office', 'content_creation', 'data_analysis', 'design', 'marketing', 'security', 'education'],
  data: ['data_analysis', 'development', 'education'],
  tutorials: ['education', 'development', 'content_creation', 'data_analysis', 'security'],
  community: ['education', 'development', 'marketing'],
  pending: ['development', 'office', 'content_creation', 'data_analysis', 'design', 'marketing', 'security', 'education'],
};

const LEGACY_CATEGORY_TO_L1: Record<string, CategoryL1> = {
  models: 'models',
  llm: 'models',
  llms: 'models',
  projects: 'apps',
  open_source: 'frameworks',
  platforms: 'tools',
  tools: 'tools',
  content: 'tutorials',
  articles: 'tutorials',
  community: 'community',
  communities: 'community',
  other: 'pending',
  pending: 'pending',
};

export function isCategoryL1(value: unknown): value is CategoryL1 {
  return typeof value === 'string' && (CATEGORY_L1_VALUES as readonly string[]).includes(value);
}

export function isCategoryL2(value: unknown): value is CategoryL2 {
  return typeof value === 'string' && (CATEGORY_L2_VALUES as readonly string[]).includes(value);
}

export function normalizeCategoryL1(input: unknown): CategoryL1 {
  if (isCategoryL1(input)) return input;
  if (typeof input !== 'string') return 'pending';

  const key = input.trim().toLowerCase().replace(/[\s-]+/g, '_');
  const mapped = LEGACY_CATEGORY_TO_L1[key];
  if (mapped) return mapped;
  return 'pending';
}

export function normalizeCategoryL2(input: unknown): CategoryL2 {
  if (isCategoryL2(input)) return input;
  if (typeof input !== 'string') return 'development';

  const key = input.trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (isCategoryL2(key)) return key;
  return 'development';
}

export function applyPendingByConfidence(params: {
  l1: CategoryL1;
  confidence: number;
  threshold?: number;
}): CategoryL1 {
  const threshold = Number.isFinite(params.threshold)
    ? Math.max(0, Math.min(1, params.threshold as number))
    : CATEGORY_CONFIDENCE_THRESHOLD;
  if (!Number.isFinite(params.confidence)) return 'pending';
  if (params.confidence < threshold) return 'pending';
  return params.l1;
}

export function sortL1ByPriority(candidates: CategoryL1[]): CategoryL1[] {
  const rank = new Map(CATEGORY_L1_PRIORITY.map((value, index) => [value, index]));
  return [...candidates].sort((a, b) => (rank.get(a) ?? 999) - (rank.get(b) ?? 999));
}
